import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Megaphone, ArrowRight } from 'lucide-react';
import { useDynamicTranslateMany } from '@/hooks/useDynamicTranslate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SponsoredPromoWidget — sidebar CTA card that promotes "Buy a sponsored
 * article". Price + discount come from admin config. Clicking leads to
 * /sponzorirani-clanak for the self-serve flow.
 */
export default function SponsoredPromoWidget() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/sponsored-settings`).then(r => r.ok ? r.json() : null).then(setSettings).catch(() => {});
  }, []);

  const title = settings?.title || t('sponsored.widget_title');
  const subtitle = settings?.subtitle || t('sponsored.widget_subtitle');
  const dyn = useDynamicTranslateMany([title, subtitle]);
  const trTitle = dyn[title] || title;
  const trSubtitle = dyn[subtitle] || subtitle;

  if (!settings || !settings.enabled) return null;
  const discount = parseFloat(settings.discount_pct || 0);
  const eff = settings.effective_price_eur ?? settings.price_eur;

  return (
    <Link to="/sponzorirani-clanak" data-testid="sponsored-promo-widget"
          className="group relative block bg-gradient-to-br from-red-600 via-red-500 to-amber-500 text-white rounded-2xl overflow-hidden shadow-lg hover:shadow-red-500/30 transition-all duration-300">
      {/* Decorative grain overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_-20%,rgba(255,255,255,0.25),transparent_60%)]" aria-hidden="true" />
      {discount > 0 && settings.badge_label && (
        <span className="absolute top-3 right-3 bg-white text-red-600 text-[11px] font-bold px-2.5 py-1 rounded-full shadow" data-testid="promo-badge">
          {settings.badge_label} -{Math.round(discount)}%
        </span>
      )}
      <div className="relative p-5 flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Megaphone size={18} />
          <span className="text-[10px] font-bold uppercase tracking-widest">{t('sponsored.badge')}</span>
        </div>
        <h3 className="text-lg md:text-xl font-extrabold leading-tight">{trTitle}</h3>
        <p className="text-[13px] text-white/90 leading-snug">{trSubtitle}</p>
        <div className="flex items-end gap-2 mt-1">
          {discount > 0 && (
            <span className="text-xs line-through text-white/70">€ {Number(settings.price_eur).toFixed(2)}</span>
          )}
          <span className="text-2xl font-black">€ {Number(eff).toFixed(2)}</span>
        </div>
        <div className="inline-flex items-center gap-1.5 mt-1 text-[13px] font-semibold bg-white/15 backdrop-blur rounded-full px-4 py-2 self-start group-hover:bg-white group-hover:text-red-600 transition-colors">
          {t('sponsored.buy_now')} <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}
